import { Box, Divider, Grid, IconButton, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import AcxSelect from "components/UI/Select/BaseSelectComponents/AcxSelect";
import { observer } from "mobx-react";
import React from "react";
import { Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WordsPhraseStore, { IWordsPhrasesModel } from "./WordsPhrasesStore";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxButton from "components/UI/AcxButton";
import theme from "Theme/AppTheme";
import InfoIcon from "@mui/icons-material/Info";
import { SAID_WITHIN_MAX } from "constants/FullTextSearch";
import { customFilterSelectStyles } from "../../shared";

interface Props {
    item: IWordsPhrasesModel;
    index: number;
    store: WordsPhraseStore;
}

const ItemContainerGrid = styled(Grid)(({ theme }) => ({
    padding: "8px",
    backgroundColor: theme.palette.lightgray.main,
    borderRadius: "4px",
    width: "100%",
}));

const WordsPhraseItem: React.FunctionComponent<Props> = observer(
    ({ item, index, store }) => {
        const [saidWithinInputVisible, setSaidWithinInputVisible] =
            React.useState(false);
        const [saidWithinEnabled, setSaidWithinEnabled] = React.useState(false);

        React.useEffect(() => {
            if (item.searchPhrase.includes(" ") && item.value === "Contains") {
                setSaidWithinEnabled(true);
            } else {
                setSaidWithinEnabled(false);
            }
        }, [index, item.searchPhrase, item.value]);

        const listLength = store.wordsPhrasesItems.length;
        const lastItemIndex = listLength - 1;

        const nextItem =
            index !== lastItemIndex
                ? store.wordsPhrasesItems[index + 1]
                : undefined;

        const showAndOrChip = listLength !== 1 && index !== lastItemIndex;

        const isAnd = item.filterGroupId === nextItem?.filterGroupId;

        const selectOptions = [
            { label: "Contains", value: "Contains" },
            {
                label: "Does not contain",
                value: "Does not contain",
            },
            {
                label: "Starts with",
                value: "Starts with",
            },
        ];

        const hasErrorAtIndex =
            store.hasError && store.errorIndices.includes(index);

        const saidWithinError =
            !!item.saidWithin &&
            (item.saidWithin < 0 || item.saidWithin > SAID_WITHIN_MAX);

        return (
            <>
                <ItemContainerGrid>
                    <Grid
                        container
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                            "&:hover .closeButton": {
                                visibility: "visible",
                            },
                        }}
                    >
                        <AcxSelect
                            id={"words-phrases-contains-select-" + index}
                            options={selectOptions}
                            defaultValue={selectOptions.find(
                                (i) => i.value === item.value,
                            )}
                            customStyle={customFilterSelectStyles}
                            onChange={(item) => {
                                store.updateRBCItemContains(index, item.value);
                            }}
                        />

                        {index !== 0 && (
                            <IconButton
                                onClick={() =>
                                    store.removeWordsPhrasesItem(index)
                                }
                                size="small"
                                className="closeButton"
                                sx={{ visibility: "hidden" }}
                            >
                                <CloseIcon />
                            </IconButton>
                        )}
                    </Grid>
                    <AcxMainTextField
                        id={"words-phrases-text-field-" + index}
                        value={item.searchPhrase}
                        onChange={(e) => {
                            store.setWordsPhrasesItemSearchPhrase(
                                index,
                                e.target.value,
                            );
                        }}
                        textContainerSx={{ paddingRight: "0.3rem" }}
                        showAllErrors
                        error={hasErrorAtIndex}
                        errorHelper={
                            hasErrorAtIndex &&
                            store.hasStandardSyntaxError(item.searchPhrase)
                                ? "Only Letters, Numbers, and Spaces are supported characters in this search"
                                : saidWithinError
                                ? '"Said Within" must be between 0 and 15'
                                : "'Starts with' only supports a single word and 'Said within' only applies to phrases or word groups of 2 or more for 'Contains'"
                        }
                    />
                    {saidWithinInputVisible && (
                        <Grid item xs={12} sx={{ paddingTop: 1 }}>
                            <AcxMainTextField
                                labelText="Said Within"
                                id={
                                    "words-phrases-said-within-text-field-" +
                                    index
                                }
                                value={item.saidWithin}
                                fullWidth
                                type="number"
                                placeholderText="# of words"
                                onChange={(e) => {
                                    store.setRuleSaidWithin(
                                        index,
                                        Number(e.target.value),
                                    );
                                }}
                                isDisabled={!saidWithinEnabled}
                                error={saidWithinError}
                                errorHelper={`Must be between 0 and ${SAID_WITHIN_MAX}`}
                            />
                        </Grid>
                    )}
                    <Grid container item xs={12} sx={{ mt: 0.5 }}>
                        <Grid item>
                            <AcxButton
                                fullWidth
                                id="words-phrases-filter-add-condition-button"
                                onClick={() => {
                                    store.setRuleSaidWithin(index, 0);
                                    setSaidWithinInputVisible((prev) => !prev);
                                }}
                                color="white"
                                sx={{
                                    backgroundColor: "transparent",
                                    color: theme.palette.blue.main,
                                    border: "none",
                                    px: 0.25,
                                    "&:disabled": {
                                        backgroundColor: "transparent",
                                    },
                                }}
                                disabled={
                                    !saidWithinInputVisible &&
                                    !saidWithinEnabled
                                }
                            >
                                {saidWithinInputVisible ? "-" : "+"} Condition
                            </AcxButton>
                        </Grid>
                        <Tooltip
                            title={`Conditions are allowed for 'Contains' inputs with multiple words. \n'Said Within' must be between 0 and ${SAID_WITHIN_MAX} and only applies to phrases or word groups of 2 or more. \nIf you'd like to search for exact phrases do not use a condition.`}
                        >
                            <Grid container item xs={1} alignItems="center">
                                <InfoIcon
                                    sx={{
                                        height: "16px",
                                        width: "16px",
                                        marginLeft: 0.5,
                                    }}
                                    color="primary"
                                />
                            </Grid>
                        </Tooltip>
                    </Grid>
                </ItemContainerGrid>
                {showAndOrChip && (
                    <Grid
                        container
                        sx={{
                            marginTop: "12px",
                            marginBottom: "12px",
                            height: "28px",
                            position: "relative",
                        }}
                        justifyContent={isAnd ? "flex-start" : "center"}
                        alignItems="center"
                    >
                        {!isAnd && (
                            <Divider
                                sx={{
                                    width: "100%",
                                    backgroundColor:
                                        theme.palette.lightBlue.background,
                                    height: "2px",
                                }}
                            />
                        )}
                        <Stack
                            direction="row"
                            sx={{
                                color: "#1E40AF",
                                backgroundColor:
                                    theme.palette.lightBlue.background,
                                borderRadius: "4px",
                                padding: "5px 10px 5px 10px",
                                position: "absolute",
                                cursor: "pointer",
                            }}
                            alignItems={"center"}
                            onClick={() => {
                                store.changeCombinator(index, !isAnd ? 0 : 1);
                            }}
                        >
                            <Box fontWeight={"bold"}>
                                {isAnd ? "AND" : "OR"}
                            </Box>
                        </Stack>
                    </Grid>
                )}
            </>
        );
    },
);

export default WordsPhraseItem;
